import { useContext, useCallback } from 'react';
import { Box, Typography, Link, Icon, TextButton, Form } from 'bonfire-kindling';
import { Belt, Page, Container, Stepper } from 'components';
import { ToastContext } from 'contexts';
import { Trans, useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router';
import { maxPasswordLength, minPasswordLength } from 'constants/settings';
import { checkPasswordStrength } from 'helpers/validation';
import useRegister from './hooks/useRegister';
import useRegistrationStepperProps from '../../hooks/useRegistrationStepperProps';

const isValidPassword = password =>
  !(password.length < minPasswordLength || password.length > maxPasswordLength);

export default () => {
  const { t } = useTranslation();
  const toastContext = useContext(ToastContext);
  const stepperProps = useRegistrationStepperProps();
  const { state: { registrationToken, email } = {} } = useLocation();
  const { mutate: register, isLoading, isError, isSuccess, error } = useRegister();

  const onSubmit = useCallback(
    async ({ firstName, lastName, password, confirmPassword }) => {
      const errors = {};
      if (!firstName || !firstName.trim()) {
        errors.firstName = t('login.confirm.first_required');
      }
      if (!lastName || !lastName.trim()) {
        errors.lastName = t('login.confirm.last_required');
      }
      if (!isValidPassword(password)) {
        errors.password = t('login.confirm.password_length', {
          minLength: minPasswordLength,
          maxLength: maxPasswordLength,
        });
      }
      if (confirmPassword !== password) {
        errors.confirmPassword = t('login.confirm.no_match');
      }
      if (Object.keys(errors).length > 0) {
        return errors;
      }

      const isPasswordStrong = await checkPasswordStrength(password, [firstName, lastName, email]);
      if (!isPasswordStrong) {
        toastContext.triggerToast({
          message: (
            // These two strings are split into seperate fragments so that the &nbsp; will be rendered.
            // It does not show if the message is stored as a single translation.
            <>
              {t('login.you_cannot_use_this_password_error')}&nbsp;
              <Trans i18nKey="login.why_am_i_seeing_this_error">
                <Link
                  href="https://vendorsupport.gobonfire.com/hc/en-us/articles/6831723396247-Why-am-I-seeing-a-You-cannot-use-this-password-error-"
                  openInNew
                >
                  Why am I seeing this error?
                </Link>
              </Trans>
            </>
          ),
        });
        return;
      }

      register({ registrationToken, firstName, lastName, password });
    },
    [email, t, toastContext, register, registrationToken]
  );

  if (!registrationToken) {
    return <Redirect to="/" />;
  }

  if (isError) {
    toastContext.triggerToast({ message: error.message });
  }

  if (isSuccess) {
    toastContext.triggerToast({ message: t('login.register_success'), variant: 'success' });
    return <Redirect to="/login" />;
  }

  return (
    <Page>
      <Container>
        <Box mt={4} mb={4}>
          <Stepper {...stepperProps} completeIndex={0} activeIndex={1} />
        </Box>
        <Box bgcolor="blue.light" mt={4} p={5} sx={{ width: { xs: 300, sm: 500 } }}>
          <Form
            onSubmit={onSubmit}
            initialValues={{
              firstName: '',
              lastName: '',
              password: '',
              confirmPassword: '',
            }}
          >
            {({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit}>
                <Typography variant="h3" component="h3" align="center">
                  {t('login.confirm.complete_setup')}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" my={3}>
                  <Icon iconName="envelope" style={{ fontSize: '1.25rem' }} />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    {email}
                  </Typography>
                </Box>
                <Belt>
                  <Form.TextField
                    name="firstName"
                    label={t('login.confirm.first')}
                    margin="dense"
                    sx={{ mr: 3 }}
                    fullWidth
                    required
                  />
                  <Form.TextField
                    name="lastName"
                    label={t('login.confirm.last')}
                    margin="dense"
                    fullWidth
                    required
                  />
                </Belt>
                <Form.TextField
                  name="password"
                  type="password"
                  label={t('login.confirm.password')}
                  margin="dense"
                  required
                  fullWidth
                />
                <Form.TextField
                  name="confirmPassword"
                  type="password"
                  label={t('login.confirm.confirm_password')}
                  margin="dense"
                  required
                  fullWidth
                />
                <Box mt={4} display="flex" justifyContent="center" alignItems="center">
                  <TextButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    isBusy={submitting || isLoading}
                    label={t('login.confirm.continue')}
                  />
                </Box>
              </form>
            )}
          </Form>
        </Box>
      </Container>
    </Page>
  );
};
